import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";
import { startLatencySpan } from "@/lib/session-latency-trace";

import { isContinuousVoiceActive } from "./continuous-voice-store";
import { isDictationAnywhereBusy } from "./dictation-anywhere/dictation-anywhere-store";
import {
  captureScreenshotDisplayAsFile,
  type ScreenshotPreview,
} from "./use-screenshot-capture";
import { getWindowDisplayId, getWindowRoleSync } from "./use-window-role";
import {
  type VoiceAttentionChapter,
  useVoiceAttentionStore,
  voiceAttentionElapsedMs,
} from "./voice-attention-store";

export type VoiceAttentionChapterCapture = {
  chapterId: string;
  file: File;
  screen: ScreenshotScreenMeta;
  preview: ScreenshotPreview | null;
};

type CaptureRecord = {
  recordingId: string;
  chapterId: string;
  promise: Promise<void>;
  capture?: VoiceAttentionChapterCapture;
};

/** Above the observed 25–230 ms capture transport: a capture whose command
 *  started this long before the view began changing grabbed the pre-scroll
 *  pixels, so ink is never burned onto scrolled content. */
const CAPTURE_GRAB_MARGIN_MS = 250;

const records = new Map<string, CaptureRecord>();
let captureQueue = Promise.resolve();

function recordKey(recordingId: string, chapterId: string): string {
  return `${recordingId}/${chapterId}`;
}

function isCaptureCoordinator(): boolean {
  // Continuous voice and dictation anywhere finals are handled only by the
  // owner webview. Keep their capture records in that same JS context even
  // when speech began on a mirror display; the owner still captures the
  // chapter's explicit display.
  if (isContinuousVoiceActive() || isDictationAnywhereBusy()) {
    return getWindowRoleSync() === "owner";
  }
  const activationDisplayId =
    useVoiceAttentionStore.getState().activation?.display_id;
  return activationDisplayId === null || activationDisplayId === undefined
    ? getWindowRoleSync() === "owner"
    : getWindowDisplayId() === activationDisplayId;
}

function chapterIsStable(chapter: VoiceAttentionChapter): boolean {
  const state = useVoiceAttentionStore.getState();
  const view = state.views[String(chapter.displayId)];
  return Boolean(
    state.recordingId &&
    view &&
    view.revision === chapter.revision &&
    !view.changing,
  );
}

/** Whether a capture whose command started at `commandStartMs` still shows
 *  the chapter, judged by the chapter's latest state in the store. */
function captureShowsChapter(
  recordingId: string,
  chapterId: string,
  commandStartMs: number | undefined,
): boolean {
  const latest = useVoiceAttentionStore.getState();
  if (latest.recordingId !== recordingId) return false;
  const chapter = latest.chapters.find(
    (candidate) => candidate.id === chapterId,
  );
  if (!chapter) return false;
  if (chapter.closedMs === undefined) return chapterIsStable(chapter);
  return (
    commandStartMs !== undefined &&
    chapter.closedMs >= commandStartMs + CAPTURE_GRAB_MARGIN_MS
  );
}

export function requestVoiceAttentionChapterCapture(
  chapter: VoiceAttentionChapter,
): void {
  const state = useVoiceAttentionStore.getState();
  const recordingId = state.recordingId;
  if (!recordingId || !isCaptureCoordinator() || !chapterIsStable(chapter)) {
    return;
  }
  const key = recordKey(recordingId, chapter.id);
  if (records.has(key)) return;

  const record: CaptureRecord = {
    recordingId,
    chapterId: chapter.id,
    promise: Promise.resolve(),
  };
  record.promise = captureQueue
    .catch(() => {})
    .then(async () => {
      if (!chapterIsStable(chapter)) return;
      let commandStartMs: number | undefined;
      const result = await captureScreenshotDisplayAsFile(
        chapter.displayId,
        () => {
          commandStartMs = voiceAttentionElapsedMs();
        },
      );
      if (
        !captureShowsChapter(recordingId, chapter.id, commandStartMs) ||
        !result.file ||
        !result.screen
      ) {
        return;
      }
      record.capture = {
        chapterId: chapter.id,
        file: result.file,
        screen: result.screen,
        preview: result.preview ?? null,
      };
    })
    .catch(() => {})
    .finally(() => {
      if (!record.capture && records.get(key) === record) records.delete(key);
    });
  captureQueue = record.promise;
  records.set(key, record);
}

export function pruneVoiceAttentionChapterCaptures(
  recordingId: string,
  chapterIds: string[],
): void {
  for (const chapterId of chapterIds) {
    records.delete(recordKey(recordingId, chapterId));
  }
}

export async function awaitVoiceAttentionChapterCaptures(
  recordingId: string | null,
  timeoutMs = 2_500,
  chapterIds?: string[],
): Promise<VoiceAttentionChapterCapture[]> {
  if (!recordingId) return [];
  const matching = [...records.values()].filter(
    (record) => record.recordingId === recordingId,
  );
  if (matching.length === 0) return [];
  const finishWait = startLatencySpan("drawing.capture_wait", {
    recordingId,
    count: matching.length,
    timeoutMs,
  });
  let timedOut = false;
  let timeoutId: number | undefined;
  try {
    await Promise.race([
      Promise.allSettled(matching.map((record) => record.promise)),
      new Promise<void>((resolve) => {
        timeoutId = window.setTimeout(() => {
          timedOut = true;
          resolve();
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    finishWait({
      timedOut,
      count: matching.filter((record) => record.capture).length,
    });
  }
  const orderedChapterIds =
    chapterIds ??
    useVoiceAttentionStore.getState().chapters.map((chapter) => chapter.id);
  const chapterOrder = new Map(
    orderedChapterIds.map((chapterId, index) => [chapterId, index]),
  );
  return matching
    .flatMap((record) => (record.capture ? [record.capture] : []))
    .sort(
      (a, b) =>
        (chapterOrder.get(a.chapterId) ?? Number.MAX_SAFE_INTEGER) -
        (chapterOrder.get(b.chapterId) ?? Number.MAX_SAFE_INTEGER),
    );
}

export function clearVoiceAttentionChapterCaptures(recordingId: string): void {
  for (const [key, record] of records) {
    if (record.recordingId === recordingId) records.delete(key);
  }
}

export function resetVoiceAttentionChapterCaptures(): void {
  records.clear();
  captureQueue = Promise.resolve();
}
